import type { ComponentType } from 'react';

import type { Input, InputState } from '@burnsred/entity-form';

import type { GenericInputDebounced } from './GenericInputDebounced';
import { filterProps } from '../utils/filterProps';

/**
 * Note: also receives {@link InputState} when rendered by {@link Input}.
 */
export type GenericInputProps<P extends object = Record<string, unknown>> = {
  /** The Chakra component that will be rendered */
  Component: ComponentType<P>;
  className?: string;
} & P;

/**
 * Composes a presentational Component to be passed to {@link Input}, filtering
 * out non-DOM-safe props.
 *
 * (Use this to create your widgets, rather than passing directly to Input).
 *
 * @example
 * ```tsx
 * import { Input as ChakraInput, type InputProps as ChakraInputProps } from '@chakra-ui/react';
 *
 * export const InputWidget = (props: InputWidgetProps) => (
 *   <GenericInput<ChakraInputProps>
 *     Component={ChakraInput}
 *     className="InputWidget"
 *     {...props}
 *   />
 * );
 *
 * // In a Form:
 * <Input name="title" component={InputWidget} />
 * ```
 *
 * @see {@link GenericInputDebounced}
 */
export const GenericInput = <P extends object>({
  Component,
  className,
  ...props
}: GenericInputProps<P>) => (
  <Component className={className} {...(filterProps(props) as P)} />
);
